/**
 * Hooks de gestão de notas de voz com transcrição via OpenAI Whisper.
 *
 * - useVoiceNotes(contactId)         → lista todas as notas do contato
 * - useCreateVoiceNote()             → upload de áudio + criação da nota + trigger transcrição
 * - useDeleteVoiceNote()             → remove nota (DB) + áudio (Storage)
 * - useRetryTranscription()          → reenvia uma nota com status 'failed' para Whisper
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

const BUCKET = 'voice-notes';

// ── Tipos ────────────────────────────────────────────────────────────────────

export type TranscriptionStatus = 'pending' | 'processing' | 'completed' | 'failed';
export type NluStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'skipped';
export type Sentiment = 'positive' | 'neutral' | 'negative' | 'mixed';

export interface MentionedEntities {
  leaders?: string[];
  places?:  string[];
  demands?: string[];
  names?:   string[];
}

export interface VoiceNote {
  id:                    string;
  tenant_id:             string;
  contact_id:            string;
  created_by:            string;
  audio_path:            string;
  mime_type:             string;
  duration_seconds:      number;
  file_size_bytes:       number;

  // Transcrição
  transcription:         string | null;
  transcription_status:  TranscriptionStatus;
  transcription_error:   string | null;
  language:              string;
  transcribed_at:        string | null;

  // NLU (Gemini)
  sentiment:              Sentiment | null;
  themes:                 string[];
  summary:                string | null;
  mentioned_entities:     MentionedEntities;
  engagement_indicator:   number | null;
  next_action_suggestion: string | null;
  nlu_status:             NluStatus;
  nlu_error:              string | null;
  analyzed_at:            string | null;

  // Metadados
  created_at:            string;
  latitude:              number | null;
  longitude:             number | null;

  // Enriquecido client-side:
  audio_url?:            string;
}

// ── List ─────────────────────────────────────────────────────────────────────

export function useVoiceNotes(contactId: string | undefined) {
  return useQuery<VoiceNote[]>({
    queryKey: ['voice-notes', contactId],
    queryFn: async (): Promise<VoiceNote[]> => {
      const { data, error } = await db
        .from('contact_voice_notes')
        .select('*')
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Gera signed URLs para o player
      const notes = (data ?? []) as VoiceNote[];
      const withUrls = await Promise.all(
        notes.map(async (n) => {
          const { data: signed } = await db.storage
            .from(BUCKET)
            .createSignedUrl(n.audio_path, 3600);
          return { ...n, audio_url: signed?.signedUrl ?? undefined };
        }),
      );

      return withUrls;
    },
    enabled: !!contactId,
    // Refetch a cada 5 s enquanto houver nota em processamento (transcrição ou NLU)
    refetchInterval: (query) => {
      const data = query.state.data;
      const hasActive = data?.some(
        (n) =>
          n.transcription_status === 'processing' ||
          n.transcription_status === 'pending' ||
          n.nlu_status === 'processing' ||
          (n.transcription_status === 'completed' && n.nlu_status === 'pending'),
      );
      return hasActive ? 5_000 : false;
    },
  });
}

// ── Create ───────────────────────────────────────────────────────────────────

interface CreateVoiceNoteInput {
  contactId:        string;
  audioBlob:        Blob;
  durationSeconds:  number;
  mimeType:         string;
  latitude?:        number | null;
  longitude?:       number | null;
}

export function useCreateVoiceNote() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateVoiceNoteInput): Promise<VoiceNote> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('Sessão expirada.');

      const userId = session.user.id;
      const tenantId =
        (session.user.app_metadata as Record<string, unknown>)?.tenant_id as string
        ?? (session.user.user_metadata as Record<string, unknown>)?.tenant_id as string;
      if (!tenantId) throw new Error('Tenant não identificado.');

      const noteId = crypto.randomUUID();
      const ext    = mimeToExt(input.mimeType);
      const path   = `${tenantId}/${input.contactId}/${noteId}.${ext}`;

      // 1. Upload do áudio
      const { error: uploadErr } = await db.storage
        .from(BUCKET)
        .upload(path, input.audioBlob, {
          contentType: input.mimeType,
          upsert: false,
        });
      if (uploadErr) throw new Error(`Upload falhou: ${uploadErr.message}`);

      // 2. Cria registro
      const { data: note, error: insertErr } = await db
        .from('contact_voice_notes')
        .insert({
          id:                noteId,
          tenant_id:         tenantId,
          contact_id:        input.contactId,
          created_by:        userId,
          audio_path:        path,
          mime_type:         input.mimeType,
          duration_seconds:  Math.round(input.durationSeconds),
          file_size_bytes:   input.audioBlob.size,
          latitude:          input.latitude ?? null,
          longitude:         input.longitude ?? null,
          transcription_status: 'pending',
        })
        .select()
        .single();

      if (insertErr || !note) {
        // Cleanup: remove o áudio se o INSERT falhou
        await db.storage.from(BUCKET).remove([path]);
        throw new Error(`Falha ao registrar nota: ${insertErr?.message ?? 'erro'}`);
      }

      // 3. Dispara transcrição em background (não await — UI mostra status)
      void supabase.functions.invoke('transcribe-audio', {
        body: { note_id: noteId },
      });

      return note as VoiceNote;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['voice-notes', vars.contactId] });
    },
  });
}

// ── Delete ───────────────────────────────────────────────────────────────────

export function useDeleteVoiceNote() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (note: VoiceNote): Promise<void> => {
      // Remove o áudio do storage
      await db.storage.from(BUCKET).remove([note.audio_path]);
      // Remove o registro (RLS valida tenant)
      const { error } = await db
        .from('contact_voice_notes')
        .delete()
        .eq('id', note.id);
      if (error) throw error;
    },
    onSuccess: (_data, note) => {
      qc.invalidateQueries({ queryKey: ['voice-notes', note.contact_id] });
    },
  });
}

// ── Retry transcription ──────────────────────────────────────────────────────

export function useRetryTranscription() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (note: VoiceNote): Promise<void> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Sessão expirada.');

      await supabase.functions.invoke('transcribe-audio', {
        body: { note_id: note.id },
      });
    },
    onSuccess: (_data, note) => {
      qc.invalidateQueries({ queryKey: ['voice-notes', note.contact_id] });
    },
  });
}

// ── Agregações para dashboards/mapa ─────────────────────────────────────────

export interface VoiceInsightsSummary {
  total_notes:     number;
  avg_engagement:  number | null;
  geo_notes:       number;
  sentiment_dist:  Partial<Record<Sentiment, number>>;
  top_themes:      Array<{ theme: string; count: number }>;
  period_days:     number;
}

export function useVoiceInsightsSummary(periodDays = 30) {
  return useQuery<VoiceInsightsSummary>({
    queryKey: ['voice-insights-summary', periodDays],
    queryFn: async (): Promise<VoiceInsightsSummary> => {
      const { data, error } = await db.rpc('voice_insights_summary', { p_days: periodDays });
      if (error) throw error;

      const raw = (data ?? {}) as Partial<VoiceInsightsSummary>;
      return {
        total_notes:     raw.total_notes    ?? 0,
        avg_engagement:  raw.avg_engagement ?? null,
        geo_notes:       raw.geo_notes      ?? 0,
        sentiment_dist:  raw.sentiment_dist ?? {},
        top_themes:      raw.top_themes     ?? [],
        period_days:     raw.period_days    ?? periodDays,
      };
    },
    staleTime: 5 * 60_000,
  });
}

export interface VoiceNoteGeoPin {
  id:                    string;
  contact_id:            string;
  latitude:              number;
  longitude:             number;
  sentiment:             Sentiment | null;
  themes:                string[];
  summary:               string | null;
  engagement_indicator:  number | null;
  created_at:            string;
}

export function useVoiceNotesGeoPins(periodDays = 90) {
  return useQuery<VoiceNoteGeoPin[]>({
    queryKey: ['voice-notes-geo-pins', periodDays],
    queryFn: async (): Promise<VoiceNoteGeoPin[]> => {
      const { data, error } = await db.rpc('voice_notes_geo_pins', { p_days: periodDays });
      if (error) throw error;
      return (data ?? []) as VoiceNoteGeoPin[];
    },
    staleTime: 5 * 60_000,
  });
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function mimeToExt(mime: string): string {
  if (mime.includes('webm')) return 'webm';
  if (mime.includes('mp4'))  return 'm4a';
  if (mime.includes('mpeg')) return 'mp3';
  if (mime.includes('ogg'))  return 'ogg';
  if (mime.includes('wav'))  return 'wav';
  return 'webm';
}
